import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "styled-components";
import { useDialog } from "../../../../hooks";
import usePages from "../../../../hooks/use-pages";
import { CandidateActionTypes } from "../../../../models/reason.model";
import { AppState } from "../../../../store/app-state";
import {
  candidateHired,
  candidateNotInterviewed,
  declineCandidate,
  denyJobOffer,
  fetchDeclineCandidateReasons,
} from "../../../../store/candidate/candidate-actions";
import { Text } from "../../../common/text/text";
import Button from "../../../form/button/button";
import Modal from "../../../modal/modal";
import SelectComponent, { Option } from "../../../onboarding/select";
import { SectionBlock, SectionList, Title } from "../../talent.styled";
import { ActionsList, ModalReasonsCointainer } from "./actions.styled";

const modalTalent = document.getElementById("modal-talent");

interface ActionsProps {
  talentId: string;
  jobSetupId?: string;
  trackId: string;
  isLocked: boolean;
  onUnlockCandidate: () => void;
  onNotAfordable: () => void;
}

const Actions: React.FC<ActionsProps> = ({
  talentId,
  jobSetupId,
  trackId,
  isLocked,
  onUnlockCandidate,
  onNotAfordable,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("private-home-page");
  const { isTrack } = usePages();
  const defaultReason: Option = useMemo(() => {
    return {
      value: "1",
      label: t("talent.selectReason"),
    };
  }, [t]);

  const { createDialog } = useDialog();
  const dispatch = useDispatch();
  const [reasonType, setReasonType] = useState<
    CandidateActionTypes | null | undefined
  >();
  const [selectedReason, setSelectedReason] = useState<Option>(
    defaultReason as Option
  );
  const loadingUnlockCandidate = useSelector(
    (state: AppState) => state.candidateState.unlockCandidateData.loading
  );
  const declineCandidateReasonsData = useSelector(
    (state: AppState) => state.candidateState.declineCandidateReasonsData
  );

  const reasonsOptions: Option[] = useMemo(() => {
    const options = declineCandidateReasonsData.data
      ? declineCandidateReasonsData.data
          .filter((data) => data.type === reasonType)
          .map((reason) => ({
            value: reason.id,
            label: reason.name,
          }))
      : [];
    return [defaultReason, ...options];
  }, [declineCandidateReasonsData, reasonType, defaultReason]);

  useEffect(() => {
    dispatch(fetchDeclineCandidateReasons());
  }, [dispatch]);

  useEffect(() => {
    if (!!reasonType) {
      setSelectedReason(defaultReason);
    }
  }, [reasonType, defaultReason]);

  const confirmNotifyToCandidate = useCallback(() => {
    return createDialog({
      title: t("talent.attentionRecruiter"),
      message: t("talent.confirmTalentNotification"),
      top: 0,
    });
  }, [t, createDialog]);

  const handleHired = useCallback(async () => {
    const confirmed = await confirmNotifyToCandidate();

    if (confirmed) {
      dispatch(candidateHired(talentId, trackId));
    }
  }, [talentId, trackId, dispatch, confirmNotifyToCandidate]);

  const handleNotApproved = useCallback(async () => {
    const confirmed = await confirmNotifyToCandidate();

    if (confirmed && selectedReason.value !== defaultReason.value) {
      dispatch(declineCandidate(talentId, trackId, selectedReason.value));
    }
  }, [
    confirmNotifyToCandidate,
    selectedReason.value,
    dispatch,
    talentId,
    trackId,
    defaultReason,
  ]);

  const handleNotInterviewed = useCallback(async () => {
    const confirmed = await confirmNotifyToCandidate();

    if (confirmed) {
      dispatch(
        candidateNotInterviewed(talentId, trackId, selectedReason.value)
      );
    }
  }, [
    talentId,
    trackId,
    dispatch,
    confirmNotifyToCandidate,
    selectedReason.value,
  ]);

  const handleDenyJobOffer = useCallback(async () => {
    const confirmed = await confirmNotifyToCandidate();

    if (confirmed && selectedReason.value !== defaultReason.value) {
      dispatch(denyJobOffer(talentId, trackId, selectedReason.value));
    }
  }, [
    talentId,
    trackId,
    selectedReason,
    dispatch,
    confirmNotifyToCandidate,
    defaultReason,
  ]);

  const handleClickModal = useCallback(() => {
    setReasonType(null);
    if (reasonType === CandidateActionTypes.NOT_APPROVED) {
      handleNotApproved();
    }
    if (reasonType === CandidateActionTypes.DECLINED_PROPOSAL) {
      handleDenyJobOffer();
    }
    if (reasonType === CandidateActionTypes.NOT_INTERVIEWED) {
      handleNotInterviewed();
    }
  }, [handleDenyJobOffer, handleNotApproved, handleNotInterviewed, reasonType]);

  const label = useMemo(() => {
    switch (reasonType) {
      case CandidateActionTypes.NOT_APPROVED:
        return t("talent.whyNotApproved");
      case CandidateActionTypes.DECLINED_PROPOSAL:
        return t("talent.whyDenyJobOffer");
      case CandidateActionTypes.NOT_INTERVIEWED:
        return t("talent.whyNotInterview");
    }
  }, [reasonType, t]);

  return (
    <>
      <Modal
        width={600}
        isShown={!!reasonType}
        toggle={() => setReasonType(null)}
        target={modalTalent}
        background={"#fff"}
        zIndex={100}
      >
        <ModalReasonsCointainer>
          <Text
            fontSize={24}
            fontWeight={700}
            color={theme.colors.primaryBlue.main}
            id="modal-title"
          >
            {label}
          </Text>
          <SelectComponent
            currentOption={selectedReason}
            setOption={setSelectedReason}
            options={reasonsOptions}
          />
          <Button
            onClick={handleClickModal}
            color="primaryBlue"
            disabled={selectedReason.value === defaultReason.value}
          >
            {t("continue")}
          </Button>
        </ModalReasonsCointainer>
      </Modal>

      {isLocked ? (
        <SectionBlock>
          <Title>{t("talent.candidateStatus")}</Title>
          <ActionsList>
            <SectionList id="unlock-candidate">
              <Button
                color="candidateHired"
                onClick={onUnlockCandidate}
                loading={loadingUnlockCandidate}
              >
                {t("talent.unlockCandidate")}
              </Button>
              {isTrack && !!jobSetupId && (
                <Button
                  color="candidateNotInterview"
                  onClick={onNotAfordable}
                  loading={loadingUnlockCandidate}
                >
                  {t("talent.notSuitable")}
                </Button>
              )}
            </SectionList>
          </ActionsList>
        </SectionBlock>
      ) : (
        <>
          <SectionBlock>
            <Title>{t("talent.enterTheCandidateStatus")}</Title>
            <ActionsList>
              <Button
                color="candidateHired"
                onClick={() => {
                  handleHired();
                }}
              >
                {t("talent.hiredCandidate")}
              </Button>
              <Button
                color="candidateNotInterview"
                onClick={() => {
                  setReasonType(CandidateActionTypes.NOT_INTERVIEWED);
                }}
              >
                {t("talent.notInterviewed")}
              </Button>
              <Button
                color="candidateNotApproved"
                onClick={() => {
                  setReasonType(CandidateActionTypes.NOT_APPROVED);
                }}
              >
                {t("talent.notApproved")}
              </Button>
              <Button
                color="candidateDenyJobOffer"
                onClick={() => {
                  setReasonType(CandidateActionTypes.DECLINED_PROPOSAL);
                }}
              >
                {t("talent.candidateDidNotAcceptOffer")}
              </Button>
            </ActionsList>
          </SectionBlock>
        </>
      )}
    </>
  );
};

export default Actions;
